import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import MainActions from "@/components/MainActions";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import {ATTITUDES} from "@/utils/scoutingData";
import {
	COMPONENT_KINDS,
	INDEX_GROUPS,
	indexFormula,
	weightsTotal,
} from "@/utils/scoutingIndices";
import {METRIC_OPTIONS} from "@/utils/wyscoutImport";

import "./index.scss";

const ATTITUDE_OPTIONS = ATTITUDES.map((a) => ({
	value: a.id,
	label: a.id + " · " + a.label,
}));

const option = (options, value) =>
	options.find((o) => o.value === value) || null;

const NEW_COMPONENT = {
	label: "",
	weight: 0,
	kind: "per90",
	num: null,
	min: 0,
	max: 1,
};

// Modifica di un indice: componenti con peso, metrica dell'export e scala [min, max].
export default function ScoutingIndexEditor({
	index,
	onChange,
	errors = [],
	disabled = false,
}) {
	const components = index?.components || [];
	const total = weightsTotal(index);
	const setComponents = (next) => onChange({...index, components: next});
	const update = (i, changes) =>
		setComponents(components.map((c, k) => (k === i ? {...c, ...changes} : c)));
	const numberField = (i, key, label) => (
		<VLFTextField
			disabled={disabled}
			label={label}
			type="number"
			size="small"
			value={components[i][key] ?? ""}
			onChange={(e) =>
				update(i, {
					[key]: e.target.value === "" ? null : Number(e.target.value),
				})
			}
			inputProps={{"aria-label": label + " " + (i + 1), step: "any"}}
		/>
	);

	return (
		<div className="index-editor">
			<div className="ixe-head">
				<div>
					<VLFTextField
						disabled={disabled}
						label="Nome dell'indice"
						required
						placeholder="Es. Pericolosità"
						value={index?.label || ""}
						onChange={(e) => onChange({...index, label: e.target.value})}
						size="small"
						inputProps={{"aria-label": "Nome dell'indice"}}
						error={errors.includes("label")}
					/>
				</div>
				<div>
					<VLFSelectNew
						isDisabled={disabled}
						label="Per"
						options={INDEX_GROUPS}
						value={option(INDEX_GROUPS, index?.group)}
						onChange={(o) => o && onChange({...index, group: o.value})}
						sorted={false}
						isClearable={false}
						noMargin
					/>
				</div>
				<div>
					<VLFSelectNew
						isDisabled={disabled}
						label="Attitudine collegata"
						placeholder="Nessuna"
						options={ATTITUDE_OPTIONS}
						value={option(ATTITUDE_OPTIONS, index?.attitude)}
						onChange={(o) => onChange({...index, attitude: o?.value || null})}
						sorted={false}
						noMargin
					/>
				</div>
			</div>

			<ol className="ixe-components">
				{components.map((comp, i) => (
					<li
						key={i}
						className={
							"ixe-component" + (errors.includes(i) ? " has-error" : "")
						}
					>
						<div className="ixe-component__head">
							<strong>Componente {i + 1}</strong>
							<IconButton
								disabled={disabled}
								size="small"
								aria-label={"Elimina componente " + (i + 1)}
								onClick={() =>
									setComponents(components.filter((_, k) => k !== i))
								}
							>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</div>
						<div className="ixe-component__grid">
							<div className="ixe-component__wide">
								<VLFTextField
									disabled={disabled}
									label="Nome"
									placeholder="Es. Gol/90"
									size="small"
									value={comp.label}
									onChange={(e) => update(i, {label: e.target.value})}
									inputProps={{"aria-label": "Nome componente " + (i + 1)}}
								/>
							</div>
							<div>{numberField(i, "weight", "Peso %")}</div>
							<div className="ixe-component__wide">
								<VLFSelectNew
									isDisabled={disabled}
									label="Calcolo"
									options={COMPONENT_KINDS}
									value={option(COMPONENT_KINDS, comp.kind)}
									onChange={(o) =>
										o &&
										update(i, {kind: o.value, den: undefined, sub: undefined})
									}
									sorted={false}
									isClearable={false}
									noMargin
								/>
							</div>
							<div className="ixe-component__wide">
								<VLFSelectNew
									isDisabled={disabled}
									label={comp.kind === "per90" ? "Metrica" : "Metrica A"}
									options={METRIC_OPTIONS}
									value={option(METRIC_OPTIONS, comp.num)}
									onChange={(o) => update(i, {num: o?.value || null})}
									noMargin
								/>
							</div>
							{comp.kind !== "per90" && (
								<div className="ixe-component__wide">
									<VLFSelectNew
										isDisabled={disabled}
										label={
											comp.kind === "ratio" ? "Su metrica B" : "Meno metrica B"
										}
										options={METRIC_OPTIONS}
										value={option(
											METRIC_OPTIONS,
											comp.kind === "ratio" ? comp.den : comp.sub,
										)}
										onChange={(o) =>
											update(
												i,
												comp.kind === "ratio"
													? {den: o?.value || null}
													: {sub: o?.value || null},
											)
										}
										noMargin
									/>
								</div>
							)}
							<div>{numberField(i, "min", "Scala min")}</div>
							<div>{numberField(i, "max", "Scala max")}</div>
						</div>
					</li>
				))}
			</ol>

			<div className="ixe-foot">
				<span
					className={"ixe-weights" + (total === 100 ? " is-ok" : " is-wrong")}
					role="status"
				>
					Somma dei pesi: {total}%{total === 100 ? "" : " (deve essere 100%)"}
				</span>
				<MainActions
					actions={[
						{
							icon: <AddIcon />,
							label: "Aggiungi componente",
							disabled,
							title: "Aggiungi componente",
							onClick: () => setComponents([...components, {...NEW_COMPONENT}]),
						},
					]}
				/>
			</div>
			{errors.includes("components") && (
				<p className="ixe-error">Serve almeno una componente.</p>
			)}

			{components.length > 0 && (
				<div className="ixe-formula">
					<span>Formula</span>
					{indexFormula(index)}
				</div>
			)}
		</div>
	);
}
